/**
 * Render-side scenario test helpers.
 *
 * Deterministic, framework-agnostic helpers used by
 * `apps/api/tests/scenarios/runner.render.test.ts` to assert
 * production-driven render-quality budgets.
 *
 * No LLM in the loop (per design); each helper is a pure string
 * function so CI is fully reproducible.
 *
 * @module
 */

/**
 * Re-export of the existing token estimator so scenario expectations
 * are measured by the same function the formatter uses for its own
 * truncation budgets. Swapping this anywhere requires re-baselining
 * every fixture's `max_tokens`.
 */
export { estimateTextTokens as approxTokenCount } from './truncation.js';

/**
 * Patterns that look like personally-identifiable information.
 *
 * Used by {@link findFabricatedPii}: a PII-looking token in the
 * render that does NOT appear in the substrate is a fabrication
 * (the formatter invented it). Tokens that DO appear in substrate
 * are authentic page content and pass through.
 */
const PII_PATTERNS: ReadonlyArray<{ name: string; re: RegExp }> = [
  // RFC-5322ish; deliberately permissive.
  { name: 'email', re: /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g },
  // 13–19 digit groups optionally separated by spaces or dashes.
  { name: 'card-like', re: /\b(?:\d[ -]?){13,19}\b/g },
  // SSN-ish: NNN-NN-NNNN with optional spaces/dashes.
  { name: 'ssn-like', re: /\b\d{3}[ -]?\d{2}[ -]?\d{4}\b/g },
];

export interface FabricatedPiiHit {
  pattern: string;
  value: string;
}

/**
 * Return PII-looking tokens present in `rendered` that are NOT
 * substrings of `substrateText`. These are fabrications — the
 * substrate is the source of truth, so any "PII" the renderer
 * emits without backing came from somewhere it shouldn't.
 *
 * Substrate authenticity check is a substring match, not a token
 * match, so split-up content (e.g. an email in a CSV-like substrate
 * line) still resolves.
 */
export function findFabricatedPii(
  rendered: string,
  substrateText: string
): FabricatedPiiHit[] {
  const out: FabricatedPiiHit[] = [];
  for (const { name, re } of PII_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(rendered)) !== null) {
      const value = m[0];
      if (!substrateText.includes(value)) {
        out.push({ pattern: name, value });
      }
    }
  }
  return out;
}
